import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthenticationService } from "../../services/authentication.service";
import { Sidenav, Nav, Image, Tooltip, Whisper } from "rsuite";
import { roles } from "../../utils/constants";
import { createSafeInternalUrl } from "../../utils/helpers";
import DarkModeToggle from "../dark-mode-toggle";
import { BESTSTART_LOGO_WHITE_SMALL, LEAF_LOGO } from "../../utils/constants/images";

import { version } from "../../../package.json";
import { IoHome } from "react-icons/io5";
import { IoMdLogOut, IoIosCloudUpload } from "react-icons/io";
import { FaFileCsv, FaMoneyBillTransfer, FaPiggyBank } from "react-icons/fa6";
import { FaUserFriends } from "react-icons/fa";
import { HiOfficeBuilding, HiDocumentReport } from "react-icons/hi";
import { GrTransaction } from "react-icons/gr";
import { CiBank } from "react-icons/ci";
import { useTheme } from "../../contexts/ThemeContext";
import { Hamburger } from "..";
import { AppSwitcher } from "@best-start/beststart.react.app-switcher";
import "./navigation.css";

const APP_SWITCHER_CONFIG_URL = "https://cdn.best-start.org/app-switcher/app-switcher.config.json";

function Navigation({ setMargin }) {
  const MIN_MARGIN = 50;
  const MAX_MARGIN = 200;
  const navRef = useRef(null);
  const scrollableNavRef = useRef(null);
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [activeKey, setActiveKey] = useState("1");
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);
  const navigate = useNavigate();
  const authService = useAuthenticationService();
  const { darkMode, setDarkMode } = useTheme();
  const isSupport = useMemo(() => authService.isInAnyRole(roles.SUPPORT), [authService]);
  const isAdmin = useMemo(() => authService.isInAnyRole(roles.ADMINS), [authService]);
  const isFinance = useMemo(() => authService.isInAnyRole(roles.FINANCE), [authService]);
  const isCentreAdmin = useMemo(() => authService.isInAnyRole(roles.CENTRE_ADMIN), [authService]);

  const handleSignOut = async () => {
    await authService.logout();
  };

  const paths = useMemo(
    () => [
      { icon: <IoHome className="rs-icon" />, path: "/", key: "1", name: "Home", visible: true },
      { icon: <IoIosCloudUpload className="rs-icon" />, path: "/upload", key: "2", name: "Import", visible: isAdmin },
      { icon: <CiBank className="rs-icon" />, path: "/bank-files", key: "3", name: "Bank Files", visible: isAdmin },
      { icon: <FaFileCsv className="rs-icon" />, path: "/export", key: "4", name: "Export", visible: isAdmin },
      { icon: <GrTransaction className="rs-icon" />, path: "/transactions", key: "5", name: "Transactions", visible: isSupport || isFinance },
      { icon: <FaUserFriends className="rs-icon" />, path: "/child-match", key: "6", name: "Child Match", visible: isAdmin },
      { icon: <HiOfficeBuilding className="rs-icon" />, path: "/centres", key: "7", name: "Centres", visible: isAdmin },
      { icon: <FaMoneyBillTransfer className="rs-icon" />, path: "/finance", key: "8", name: "Finance", visible: isAdmin || isFinance },
      { icon: <HiDocumentReport className="rs-icon" />, path: "/banking-report", key: "11", name: "Banking Report", visible: isCentreAdmin },
      { icon: <FaPiggyBank className="rs-icon" />, path: "/centre-banking", key: "10", name: "Centre Banking", visible: isCentreAdmin },

      {
        icon: <IoMdLogOut className="rs-icon" />,
        path: "/signout",
        key: "9",
        name: "Log Out",
        testId: "logout-button",
        footer: true,
        visible: true,
      },
    ],
    [isAdmin, isSupport, isFinance, isCentreAdmin]
  );
  function matchPath(path, activePath) {
    // Remove query params for comparison
    const cleanPath = path.split("?")[0];
    const cleanActivePath = activePath.split("?")[0];

    // Exact match
    if (cleanPath === cleanActivePath) return true;

    // Check if active path starts with the nav path (for sub-routes)
    // This allows /centres to match /centres/1, /centres/edit, etc.
    if (cleanPath !== "/" && cleanActivePath.startsWith(cleanPath + "/")) {
      return true;
    }

    return false;
  }

  const navigateTopath = useCallback(
    (path) => {
      if (location.pathname === path) {
        setExpanded(false);
        setMargin && setMargin(MIN_MARGIN);
        return;
      }
      path === "/signout" ? handleSignOut() : navigate(path);
      setExpanded(false);
      setMargin && setMargin(MIN_MARGIN);
    },
    [location.pathname, navigate, setMargin, handleSignOut]
  );

  const handleClickOutside = useCallback(
    (event) => {
          // Don't close navbar if clicking on app switcher dropdown
      if (event.target.closest('.app-switcher-dropdown') || event.target.closest('[class*="AppSwitcher"]')) {
        return;
      }
      
      if (navRef.current && !navRef.current.contains(event.target)) {
        setExpanded(false);
        setMargin && setMargin(MIN_MARGIN);
      }
    },
    [setMargin]
  );

  function onToggle(value) {
    setDarkMode(value);
  }

  function sendClick(event) {
    setExpanded(event.target.checked);
    setMargin && setMargin(event.target.checked ? MAX_MARGIN : MIN_MARGIN);
  }

  useEffect(() => {
    const activeItem = paths.find((item) => matchPath(item.path, location.pathname));
    setActiveKey(activeItem ? activeItem.key : "1");
    if (activeItem) {
      document.title = `${activeItem.name} - BestStart PayMint`;
    } else {
      document.title = "BestStart PayMint";
    }
  }, [location.pathname, paths]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleSwipe = useCallback(
    (event) => {
      // Don't interfere with AppSwitcher or other interactive elements
      if (event.target && event.target.closest &&
          (event.target.closest('.app-switcher-container') ||
           event.target.closest('[class*="AppSwitcher"]') ||
           event.target.closest('[class*="rs-dropdown"]') ||
           event.target.closest('[class*="rs-popover"]'))) {
        return;
      }

      const touch = event.touches[0];
      const swipeThreshold = 100; // Minimum distance for a swipe to be detected

      if (event.type === "touchstart") {
        navRef.current.startX = touch.clientX;
      } else if (event.type === "touchmove") {
        const deltaX = touch.clientX - navRef.current.startX;

        if (deltaX > swipeThreshold) {
          // Swipe from left to right
          setExpanded(true);
          setMargin && setMargin(MAX_MARGIN);
        } else if (deltaX < -swipeThreshold) {
          // Swipe from right to left
          setExpanded(false);
          setMargin && setMargin(MIN_MARGIN);
        }
      }
    },
    [setMargin]
  );

  useEffect(() => {
    setMargin && setMargin(expanded ? MAX_MARGIN : MIN_MARGIN);

    document.addEventListener("touchstart", handleSwipe);
    document.addEventListener("touchmove", handleSwipe);

    return () => {
      document.removeEventListener("touchstart", handleSwipe);
      document.removeEventListener("touchmove", handleSwipe);
    };
  }, [expanded, setMargin, handleSwipe]);

  const checkScrollIndicators = useCallback(() => {
    if (!scrollableNavRef.current) return;

    const element = scrollableNavRef.current;
    const { scrollTop, scrollHeight, clientHeight } = element;

    setShowTopIndicator(scrollTop > 10);
    setShowBottomIndicator(scrollTop < scrollHeight - clientHeight - 10);
  }, []);

  const handleNavScroll = useCallback(
    (event) => {
      event.preventDefault();
      const delta = event.deltaY;
      if (scrollableNavRef.current) {
        scrollableNavRef.current.scrollTop += delta;
        checkScrollIndicators();
      }
    },
    [checkScrollIndicators]
  );

  const handleIndicatorClick = useCallback(
    (direction) => {
      if (!scrollableNavRef.current) return;

      const scrollAmount = 100; // Scroll by 100px
      const currentScrollTop = scrollableNavRef.current.scrollTop;

      if (direction === "up") {
        scrollableNavRef.current.scrollTop = Math.max(0, currentScrollTop - scrollAmount);
      } else {
        scrollableNavRef.current.scrollTop = currentScrollTop + scrollAmount;
      }

      checkScrollIndicators();
    },
    [checkScrollIndicators]
  );

  const handleMouseDown = useCallback((event) => {
    if (!scrollableNavRef.current) return;

    // Don't interfere with AppSwitcher clicks
    if (event.target.closest('.app-switcher-container')) {
      return;
    }

    setIsDragging(true);
    setDragStartY(event.clientY);
    setDragStartScrollTop(scrollableNavRef.current.scrollTop);

    // Prevent text selection while dragging
    event.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging || !scrollableNavRef.current) return;

      event.preventDefault();
      const deltaY = event.clientY - dragStartY;
      const newScrollTop = dragStartScrollTop - deltaY; // Invert for natural drag feel

      scrollableNavRef.current.scrollTop = Math.max(0, newScrollTop);
      checkScrollIndicators();
    },
    [isDragging, dragStartY, dragStartScrollTop, checkScrollIndicators]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((event) => {
    if (!scrollableNavRef.current) return;

    // Don't interfere with AppSwitcher clicks
    if (event.target.closest('.app-switcher-container')) {
      return;
    }

    const touch = event.touches[0];
    setIsDragging(true);
    setDragStartY(touch.clientY);
    setDragStartScrollTop(scrollableNavRef.current.scrollTop);
  }, []);

  const handleTouchMove = useCallback(
    (event) => {
      if (!isDragging || !scrollableNavRef.current) return;

      event.preventDefault();
      const touch = event.touches[0];
      const deltaY = touch.clientY - dragStartY;
      const newScrollTop = dragStartScrollTop - deltaY;

      scrollableNavRef.current.scrollTop = Math.max(0, newScrollTop);
      checkScrollIndicators();
    },
    [isDragging, dragStartY, dragStartScrollTop, checkScrollIndicators]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    checkScrollIndicators();
    if (scrollableNavRef.current) {
      const element = scrollableNavRef.current;
      element.addEventListener("scroll", checkScrollIndicators);
      element.addEventListener("wheel", handleNavScroll, { passive: false });

      // Check on resize as well
      const resizeObserver = new ResizeObserver(checkScrollIndicators);
      resizeObserver.observe(element);

      return () => {
        element.removeEventListener("scroll", checkScrollIndicators);
        element.removeEventListener("wheel", handleNavScroll);
        resizeObserver.disconnect();
      };
    }
  }, [checkScrollIndicators, handleNavScroll, expanded]);

  // Mouse drag event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none"; // Prevent text selection

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      style={{
        position: "fixed",
        width: expanded ? MAX_MARGIN : "auto",
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        height: "100dvh",
        zIndex: 9000,
      }}
      ref={navRef}
    >
      <Sidenav expanded={expanded} className="side-bar" data-testid="navbar" data-expanded={expanded}>
        <Sidenav.Body style={{ paddingTop: 10, display: "flex", flexDirection: "column", height: "100%" }}>
          <div className={`hamburger-wrapper ${expanded ? "expanded" : ""}`}>
            <Hamburger checked={expanded} onClick={sendClick} />
          </div>

          <Whisper placement="right" trigger="hover" speaker={<Tooltip>Version: {version}</Tooltip>}>
            <div className="logo-container" aria-label={`Version: ${version}`}>
              {/* done this so it preloads the images */}
              <Image src={BESTSTART_LOGO_WHITE_SMALL} alt="BestStart Logo" width="100%" height={45} style={{ display: expanded ? "" : "none" }} />
              <Image
                src={LEAF_LOGO}
                className="leaf-logo-small"
                alt="BestStart Logo"
                width="100%"
                height={45}
                style={{ display: !expanded ? "" : "none" }}
              />
            </div>
          </Whisper>

          {/* Scrollable navigation container */}
          <div className="nav-scroll-container">
            {showTopIndicator && (
              <div className="scroll-indicator scroll-indicator-top" onClick={() => handleIndicatorClick("up")} style={{ cursor: "pointer" }}>
                <div className="scroll-indicator-gradient"></div>
              </div>
            )}

            <div
              className="scrollable-nav"
              ref={scrollableNavRef}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                cursor: isDragging ? "grabbing" : "grab",
                touchAction: "none", // Prevent default touch behaviors
              }}
            >
              <Nav activeKey={activeKey} onSelect={setActiveKey}>
                {paths &&
                  paths
                    .filter((path) => !path.footer)
                    .map((item) => {
                      return (
                        <Nav.Item
                          data-testid={`${item.testId || item.key}-web`}
                          key={item.key}
                          onClick={navigateTopath.bind(null, item.path)}
                          onAuxClick={(e) => {
                            if (e.button === 1 && item.path !== "/signout") {
                              // Prevent open redirect: only allow paths starting with / and without protocols
                              if (item.path && typeof item.path === 'string' && item.path.startsWith('/') && !item.path.startsWith('//')) {
                                const safeUrl = createSafeInternalUrl(window.location.origin, item.path);
                                if (safeUrl) {
                                  window.open(safeUrl, "_blank");
                                }
                              }
                            }
                          }}
                          eventKey={item.key}
                          icon={item.icon}
                          disabled={!item.visible}
                          style={{ userSelect: "none" }} // Prevent text selection on nav items
                        >
                          {item.name}
                        </Nav.Item>
                      );
                    })}
              </Nav>
            </div>

            {showBottomIndicator && (
              <div className="scroll-indicator scroll-indicator-bottom" onClick={() => handleIndicatorClick("down")} style={{ cursor: "pointer" }}>
                <div className="scroll-indicator-gradient"></div>
              </div>
            )}
          </div>
        </Sidenav.Body>

        {/* Fixed footer section */}
        <div className="nav-footer">
          <Nav activeKey={activeKey} onSelect={setActiveKey} className="bottom-nav">
            <DarkModeToggle onToggle={onToggle} defaultChecked={darkMode} />
            {paths &&
              paths
                .filter((path) => path.footer)
                .map((item) => {
                  return (
                    <Nav.Item
                      data-testid={`${item.testId || item.key}-footer`}
                      key={item.key}
                      onClick={navigateTopath.bind(null, item.path)}
                      onAuxClick={(e) => {
                        if (e.button === 1 && item.path !== "/signout") {
                          // Prevent open redirect: only allow paths starting with / and without protocols
                          if (item.path && typeof item.path === 'string' && item.path.startsWith('/') && !item.path.startsWith('//')) {
                            const safeUrl = createSafeInternalUrl(window.location.origin, item.path);
                            if (safeUrl) {
                              window.open(safeUrl, "_blank");
                            }
                          }
                        }
                      }}
                      eventKey={item.key}
                      icon={item.icon}
                    >
                      {item.name}
                    </Nav.Item>
                  );
                })}
          </Nav>
        </div>
      </Sidenav>
      
      {(isAdmin || isSupport || isFinance) && (
      <div className="app-switcher-container" onClick={(e) => e.stopPropagation()}>
        <AppSwitcher
          currentAppId="paymint"
          authenticationService={authService}
          expanded={expanded}
          manifestUrl={APP_SWITCHER_CONFIG_URL}
        />
      </div>
      )}
    </div>
  );
}

export default React.memo(Navigation);
